/**
 * SharedViewNotice.jsx - "Shawn Rimmell shared the view "Pipeline review"
 * with you" (round 4, 10/9/26: "it pops up on their views"). Mounted once in
 * the app shell, so the notice shows on whatever screen the person is on.
 * The server sends a share event only to the people a view was shared with
 * (routes/tableViews.js, lib/liveEvents.js `to`); each event is shown once.
 * A view of a table whose area this person cannot see is not announced (its
 * tab is closed to them, and the server never lists it for them either).
 * Their table's views menu re-reads by itself (useTableView) and shows it as New.
 */
import { useCallback, useState } from 'react';
import { Toast } from '../ui/Toast';
import { useLiveEvent } from '../../live/useLive';
import { useMyAccess } from '../../hooks/useMyAccess';

const AREA_OF = { projects: 'projects', opportunities: 'opportunities', 'ksa-projects': 'ksa', 'ksa-opportunities': 'ksa' };
const PLACE = {
  projects: 'Projects',
  opportunities: 'Opportunities',
  'ksa-projects': 'GLOBAL KSA projects',
  'ksa-opportunities': 'GLOBAL KSA opportunities',
};

/** The notice for a live event, or null when it is not a share this person should hear about. PURE. */
export function shareNotice(evt, access) {
  if (!evt || evt.kind !== 'table-views' || evt.method !== 'SHARE' || !AREA_OF[evt.sub]) return null;
  const level = access && access.areas ? access.areas[AREA_OF[evt.sub]] : 'view';
  if (!level || level === 'hidden') return null;
  const viewName = (evt.fields && evt.fields[0]) || 'a view';
  return `${evt.actor || 'Someone'} shared the view “${viewName}” with you (${PLACE[evt.sub]}).`;
}

export default function SharedViewNotice() {
  const access = useMyAccess();
  const [msg, setMsg] = useState(null);
  useLiveEvent(
    (e) => e.kind === 'table-views' && e.method === 'SHARE',
    (e) => {
      const text = shareNotice(e, access);
      if (text) setMsg({ text, key: e.seq });
    }
  );
  const done = useCallback(() => setMsg(null), []); // stable: Toast restarts its timer on a new onDone
  return msg ? <Toast key={msg.key} message={msg.text} onDone={done} /> : null;
}
